import { FC, useEffect, useRef, useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";

import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import type { AssetValidation } from "@/Features/AssetValidations/asset-validations.types";
import EditValidationForm from "../Forms/EditValidationForm";
import { getValidationLabel } from "../Forms/validationForm.utils";

// Matches CreateValidationModal so the two forms render at the same size.
const MODAL_WIDTH = 550;

interface EditValidationModalProps {
  show: boolean;
  onClose: () => void;
  // Fires after a successful save, before the modal closes. The mutation
  // hook already updates the item cache and invalidates the table's list.
  onSubmit?: () => void;
  defaultInfo: AssetValidation;
  completedByName?: string;
}

/**
 * Edit modal for a validation's scalar fields (ENG-1000). Lifted from
 * EditLabAssetModal: Cancel on a dirty form swaps the content for an
 * in-place "Discard changes?" panel; the form stays mounted (hidden) behind
 * it so "Keep editing" returns to the edits intact.
 */
export const EditValidationModal: FC<EditValidationModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
  completedByName,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const formRef = useRef<HTMLDivElement | null>(null);
  const keepEditingRef = useRef<HTMLButtonElement | null>(null);

  // Swapping the content hides the element that had focus, which would drop
  // focus to <body> inside the modal's focus trap. Re-home it.
  useEffect(() => {
    if (!show) return;
    if (confirmDiscard) {
      keepEditingRef.current?.focus();
    } else {
      formRef.current?.querySelector<HTMLElement>("input, textarea")?.focus();
    }
  }, [confirmDiscard, show]);

  const closeAndReset = () => {
    setConfirmDiscard(false);
    setDirty(false);
    onClose();
  };

  // User-initiated close. Guarded because closing mid-request would still
  // save.
  const handleClose = () => {
    if (submitting) return;
    if (dirty && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    closeAndReset();
  };

  // Success path. Deliberately unguarded: the form's submitting flag is
  // still true here (it clears in the form's finally).
  const handleSubmit = () => {
    onSubmit?.();
    closeAndReset();
  };

  return (
    <Modal open={show} onClose={handleClose}>
      {confirmDiscard ? (
        <div style={{ width: MODAL_WIDTH }}>
          <Modal.Title>Discard changes?</Modal.Title>
          <div style={{ fontWeight: 500, margin: "20px 0px" }}>
            You have unsaved changes to this validation.
          </div>
          <ButtonMenu>
            <Button size="xs" variant="subtle" onClick={handleClose}>
              Discard
            </Button>
            <Button
              size="xs"
              color="primary"
              variant="contained"
              ref={keepEditingRef}
              onClick={() => setConfirmDiscard(false)}
            >
              Keep editing
            </Button>
          </ButtonMenu>
        </div>
      ) : (
        <>
          <Modal.Title>Edit Validation</Modal.Title>
          <Modal.SubTitle style={{ marginBottom: 15 }}>
            {getValidationLabel(defaultInfo)}
          </Modal.SubTitle>
        </>
      )}

      {/* Kept mounted (hidden) behind the discard panel so "Keep editing"
          returns to the user's edits. Keyed by uuid so a host that swaps the
          record while open starts a fresh form. */}
      <div ref={formRef} style={{ display: confirmDiscard ? "none" : "block" }}>
        <EditValidationForm
          key={defaultInfo.uuid}
          width={MODAL_WIDTH}
          validation={defaultInfo}
          completedByName={completedByName}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          onSubmittingChange={setSubmitting}
          onDirtyChange={setDirty}
        />
      </div>
    </Modal>
  );
};
